import { createApp } from "vue";
import { definePreset } from "@primeuix/themes";
import "./assets/fontawesome/css/all.min.css";
import "../node_modules/prismjs/themes/prism-coy.css";
import "./assets/styles/demo/flag.css";
import "../node_modules/primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "./assets/styles/app/layout.scss";
import App from "./App.vue";
import router from "./router";
import Lara from "@primeuix/themes/lara";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import { StorageService } from "@/services/storage.service";
import ConfirmationService from "primevue/confirmationservice";
import VueNumerals from "vue-numerals";
import * as Sentry from "@sentry/vue";
import JsonExcel from "vue-json-excel3";
import BadgeDirective from "primevue/badgedirective";
import Tooltip from "primevue/tooltip";
import Ripple from "primevue/ripple";
import StyleClass from "primevue/styleclass";
import { AuthService } from "./services/auth.service";
import { CompanyService } from "./services/company.service";
import { VersionService } from "./services/version.service";

const MyPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: "{indigo.50}",
      100: "{indigo.100}",
      200: "{indigo.200}",
      300: "{indigo.300}",
      400: "{indigo.400}",
      500: "{indigo.500}",
      600: "{indigo.600}",
      700: "{indigo.700}",
      800: "{indigo.800}",
      900: "{indigo.900}",
      950: "{indigo.950}",
    },
    secondary: {
      50: "{pink.50}",
      100: "{pink.100}",
      200: "{pink.200}",
      300: "{pink.300}",
      400: "{pink.400}",
      500: "{pink.500}",
      600: "{pink.600}",
      700: "{pink.700}",
      800: "{pink.800}",
      900: "{pink.900}",
      950: "{pink.950}",
    },
  },
});

(async () => {
  await AuthService.initAuth();

  const app = createApp(App);

  app.use(PrimeVue, {
    ripple: true,
    theme: {
      preset: MyPreset,
      options: {
        darkModeSelector: false,
      },
    },
  });

  Sentry.init({
    app,
    dsn: "https://3805127821ff9a18362c4b8ed38f7504@o99442.ingest.us.sentry.io/4510107128561664",
    sendDefaultPii: true,
  });

  app.use(ToastService);
  app.use(ConfirmationService);
  app.use(VueNumerals);
  app.use(router);

  app.component("downloadExcel", JsonExcel);

  app.directive("badge", BadgeDirective);
  app.directive("tooltip", Tooltip);
  app.directive("ripple", Ripple);
  app.directive("styleclass", StyleClass);

  // $openFile(pathOrUrl): opens a stored file. Finance documents and talent
  // contracts are in the private bucket and open through a short-lived signed
  // link (StorageService.openFile).
  app.config.globalProperties.$openFile = (value) =>
    StorageService.openFile(value).catch((e) => {
      app.config.globalProperties.$toast?.add({
        severity: "error",
        summary: "File",
        detail: /not.?found|row-level|unauthori|403/i.test(String(e?.message || "")) ? "You don't have access to this file, or it no longer exists." : (e?.message || "Couldn't open the file."),
        life: 5000,
      });
    });

  AuthService.setApp(app);
  CompanyService.setApp(app);
  VersionService.setApp(app);
  app.mount("#app");

  VersionService.startPolling();
})();
